import axios from 'axios';
import ToastEventBus from 'primevue/toasteventbus';

const axiosInstance = axios.create({
    baseURL: "/api",
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
    },
    withCredentials: true,
    withXSRFToken: true,
    // Not the default XSRF-TOKEN: www.balluff.com sets one for all of
    // .balluff.com and axios would send that instead of ours (BOW-11722).
    xsrfCookieName: 'APPDB-XSRF-TOKEN'
});

/**
 * Emitted through the event bus rather than useToast(), because interceptors
 * run outside component context where the composable is unavailable.
 */
const errorToast = (summary, detail) => {
    ToastEventBus.emit('add', { severity: 'error', summary, detail, life: 10000 });
};

/**
 * A save can fail *after* the server already wrote the record, so failures are
 * never reported as "nothing was saved" — that phrasing is what drove users to
 * click again and create duplicates. See BOW-11628.
 */
const UNCERTAIN = 'Reload the page before trying again — your changes may already have been saved.';

/**
 * Reports any failure the caller does not render itself.
 *
 * Validation errors are skipped: forms bind those to the individual fields, and
 * a toast on top would report the same problem twice. Pass
 * `{ meta: { silent: true } }` on a request to opt out entirely.
 */
const reportFailure = (error) => {
    if (error.config?.meta?.silent) {
        return;
    }

    const status = error.response?.status;

    if (status === 422) {
        return;
    }

    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        errorToast('The server took too long to respond', UNCERTAIN);
        return;
    }

    if ( ! error.response) {
        errorToast('Could not reach the server', 'Check your internet connection and try again.');
        return;
    }

    if (status === 413) {
        errorToast('That file is too large', 'Pick a smaller file, or compress it before uploading.');
        return;
    }

    if (status === 403) {
        errorToast('Not allowed', 'You do not have permission to do that.');
        return;
    }

    if (status === 429) {
        errorToast('Too many requests', 'Please wait a moment and try again.');
        return;
    }

    if (status >= 500) {
        errorToast('Something went wrong on the server', UNCERTAIN);
        return;
    }

    errorToast('That did not work', error.response.data?.message || 'Please try again.');
};

export default {
    install: (app) => {
        app.config.globalProperties.$axios = axiosInstance;
        app.provide('axios', axiosInstance);

        axiosInstance.interceptors.response.use(
            (response) => response,
            (error) => {
                const status = error.response?.status;

                // 419 is an expired session, which is indistinguishable from a
                // dead button unless it is handled like a 401.
                if (status === 401 || status === 419) {
                    app.config.globalProperties.$router.push({name: '/login'});

                    return Promise.reject(error);
                }

                reportFailure(error);

                return Promise.reject(error);
            }
        );
    }
};

export { axiosInstance as axios };
